import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Users, Building2, BookOpen, Calendar, Brain, TrendingUp, DollarSign, UserPlus, Settings, ChevronRight, Clock, Menu, X, LayoutDashboard, Target, MessageSquare, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';
import ModuleSwitcher from './ModuleSwitcher';
import ManagerViewToggle from './ManagerViewToggle';

/** Resolve the CRM frontend URL — same logic as ModuleSwitcher */
function resolveCrmUrl(): string {
  const envUrl = (import.meta as any).env.VITE_CRM_URL;
  if (envUrl) return envUrl.replace(/\/$/, '');
  const { hostname, protocol } = window.location;
  if (hostname.includes('dealbox-')) {
    const crmHost = hostname.replace(/dealbox-[a-z]+-/, 'dealbox-crm-');
    return `${protocol}//${crmHost}`;
  }
  return 'http://localhost:3000';
}

interface SidebarItem {
  title: string;
  href?: string;
  icon: React.ComponentType<any>;
  children?: SidebarItem[];
  // Role-based visibility
  isExternal?: boolean;
  permission?: string; // Add granular permission key
  allowedRoles?: string[];
  requiresLeaveApproval?: boolean;
  requiresShiftSwapApproval?: boolean;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user, canApproveLeaves, canApproveShiftSwaps, isAdmin } = useAuth();
  const { isUserView, canViewTeamApprovals, canViewPayroll, canViewEmployeeLeaves, canViewLeaveCalendarAll, canViewAllEmployees } = useManagerViewPermission();
  // FIX: Start with empty object — no menu pre-opened on initial load
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [isExpanded, setIsExpanded] = useState(true); // Default to expanded
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Determine if sidebar should show labels
  const showLabels = isExpanded || isHovered;

  // Fetch company settings from API
  const { data: companySettings } = useQuery({
    queryKey: ['companySettings'],
    queryFn: async () => {
      // For now, return default settings until settings API is fully integrated
      return {
        companyName: 'HR Stack',
        logoUrl: '/logo.png',
      };
    },
  });

  // Emit sidebar width changes to CSS variable
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      showLabels ? '256px' : '80px'
    );
  }, [showLabels]);

  // Auto-open parent menus when on a child route
  useEffect(() => {
    navigationItems.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => location.pathname === child.href);
        if (hasActiveChild) {
          setOpenMenus(prev => ({ ...prev, [item.title.toLowerCase()]: true }));
        }
      }
    });
  }, [location.pathname]);

  const toggleMenu = (menuName: string) => {
    setOpenMenus(prev => ({ ...prev, [menuName]: !prev[menuName] }));
  };

  const isChildActive = (children?: SidebarItem[]) => {
    if (!children) return false;
    return children.some(child => location.pathname === child.href);
  };

  const navigationItems: SidebarItem[] = [    {
      title: 'Dashboard',
      href: '/',
      icon: LayoutDashboard,
      permission: 'dashboard'
    },
    {
      title: 'Employees',
      href: '/employees',
      icon: Users,
      permission: 'employees'
    },
    {
      title: 'Leaves',
      icon: Calendar,
      permission: 'attendance',
      children: [
        { title: 'My Leaves', href: '/leaves/my', icon: Calendar },
        { title: 'Team Approvals', href: '/leaves/approvals', icon: Calendar, requiresLeaveApproval: true },
        { title: 'Calendar', href: '/leaves/calendar', icon: Calendar }
      ]
    },
    {
      title: 'Performance',
      icon: TrendingUp,
      permission: 'performance',
      children: [
        { title: 'Goals and Review', href: '/goals-and-reviews', icon: TrendingUp },
        { title: 'Goals Management', href: '/performance/goals-management', icon: Target },
        { title: 'My Feedbacks', href: '/performance/my-feedbacks', icon: MessageSquare },
      ]
    },
    {
      title: 'Knowledge Transfers',
      icon: Brain,
      href: '/performance/knowledge-transfers',
      permission: 'performance'
    },
    {
      title: 'Payroll',
      icon: DollarSign,
      allowedRoles: ['HR', 'MANAGER', 'ADMIN'],
      permission: 'payroll',
      children: [
        { title: 'Employee Profiles', href: '/payroll/profiles', icon: DollarSign },
        { title: 'Salary Structures', href: '/payroll/salary-structures', icon: DollarSign },
        { title: 'Commission Rules', href: '/payroll/commission-rules', icon: DollarSign },
        { title: 'Payroll Runs', href: '/payroll/runs', icon: DollarSign },
        { title: 'Salary Slips', href: '/payroll/slips', icon: DollarSign },
        { title: 'Time & Attendance', href: '/payroll/time-attendance', icon: DollarSign },
        { title: 'Benefits Management', href: '/payroll/benefits', icon: DollarSign }
      ]
    },
    {
      title: 'Shift Planning',
      href: '/shift-planning',
      icon: Clock,
      permission: 'shiftPlanning'
    },
    {
      title: 'Onboarding & Offboarding',
      href: '/onboarding-offboarding',
      icon: UserPlus,
      permission: 'recruitment'
    },
    {
      title: 'Settings',
      href: `${resolveCrmUrl()}/settings?tab=hr`,
      icon: Settings,
      allowedRoles: ['ADMIN', 'MANAGER'],
      permission: 'settingsAiBot',
      isExternal: true,
    }
  ];

  // Filter navigation items based on user role
  const filteredNavigationItems = useMemo(() => {
    // Normalize role to uppercase for case-insensitive comparison
    const userRole = (user?.role || 'EMPLOYEE').toUpperCase() as 'ADMIN' | 'MANAGER' | 'HR' | 'EMPLOYEE';

    const filterItem = (item: SidebarItem): SidebarItem | null => {
      // Check role-based access
      if (item.allowedRoles && !item.allowedRoles.includes(userRole) && userRole !== 'ADMIN') {
        return null;
      }

      // Check leave approval permission
      if (item.requiresLeaveApproval && !canApproveLeaves) {
        return null;
      }

      // Check shift swap approval permission
      if (item.requiresShiftSwapApproval && !canApproveShiftSwaps) {
        return null;
      }

      if (isUserView) {
        if (item.title === 'Payroll') return null;
        if (item.title === 'Team Approvals') return null;
        if (item.title === 'Calendar' && item.href === '/leaves/calendar') return null;
        if (item.title === 'Employee Leaves') return null;
      }

   
      if (!isUserView) {
        if (item.title === 'Payroll' && !canViewPayroll) return null;
        if (item.title === 'Team Approvals' && !canViewTeamApprovals) return null;
        if (item.title === 'Calendar' && item.href === '/leaves/calendar' && !canViewLeaveCalendarAll) return null;
        if (item.title === 'Employee Leaves' && !canViewEmployeeLeaves) return null;
      }

      // Check granular module sub-permissions
      if (item.permission) {
        try {
          const modularStr = localStorage.getItem('modular_permissions');
          if (modularStr) {
             const modular = JSON.parse(modularStr);
             // Settings items live in the general group
             if (item.permission === 'settingsAiBot') {
               const general = modular.general || {};
               if (general['settingsAiBot'] === false) return null;
             } else {
               const hr = modular.hr || {};
               if (hr[item.permission] === false) return null;
             }
          }
        } catch (e) {}
      }

      // Filter children if present
      if (item.children) {
        const filteredChildren = item.children
          .map(filterItem)
          .filter((child): child is SidebarItem => child !== null);

        // If all children are filtered out, don't show parent
        if (filteredChildren.length === 0) {
          return null;
        }

        return { ...item, children: filteredChildren };
      }

      return item;
    };

    return navigationItems
      .map(filterItem)
      .filter((item): item is SidebarItem => item !== null);
  }, [user?.role, canApproveLeaves, canApproveShiftSwaps, isUserView, canViewPayroll, canViewTeamApprovals, canViewEmployeeLeaves, canViewLeaveCalendarAll]);

  const renderNavItem = (item: SidebarItem) => {
    const isActive = item.href ? location.pathname === item.href : false;
    const isMenuOpen = openMenus[item.title.toLowerCase()];
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      // FIX: isParentActive is true ONLY when one of this parent's children is the current page
      const isParentActive = isChildActive(item.children);
      // FIX: show highlight background only when a child is actually active (not just when menu is open)
      const showParentHighlight = isParentActive;
      // The icon gets the blue gradient only when a child is active
      const showIconGradient = isParentActive;

      return (
        <div key={item.title} className="space-y-1">
          <button
            onClick={() => toggleMenu(item.title.toLowerCase())}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 group",
              showLabels ? "mx-2 w-[calc(100%-16px)]" : "justify-center mx-auto w-12 px-0",
              // FIX: only light-blue background when a child page is active
              showParentHighlight
                ? "bg-[#60a5fa]/20 text-white"
                : "text-white/90 hover:text-white hover:bg-white/5"
            )}
          >
            <div className={cn(
              "p-2 rounded-lg transition-all duration-200 shrink-0",
              // FIX: blue gradient icon only when child is active, otherwise dim
              showIconGradient
                ? "bg-gradient-to-br from-[#60a5fa] to-[#3b82f6]"
                : "bg-[#2d5a8f]/40"
            )}>
              <item.icon className="h-5 w-5" />
            </div>
            {showLabels && (
              <>
                <span className="text-sm whitespace-nowrap">
                  {item.title}
                </span>
                <ChevronRight
                  className={cn(
                    "w-4 h-4 ml-auto transition-all duration-200",
                    isMenuOpen && "rotate-90"
                  )}
                />
              </>
            )}
          </button>

          {isMenuOpen && showLabels && (
            <div className="ml-4 space-y-1 border-l-2 border-white/10 pl-2 mb-2">
              {item.children!.map((child) => {
                const isChildItemActive = location.pathname === child.href;
                return (
                  <NavLink
                    key={child.href}
                    to={child.href!}
                    className={cn(
                      "flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 relative",
                      isChildItemActive
                        ? "text-white bg-[#60a5fa]/20"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    )}
                  >
                    <span>{child.title}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const linkClasses = cn(
      "flex items-center gap-3 p-3 rounded-lg transition-all duration-200 group",
      showLabels ? "mx-2 w-[calc(100%-16px)]" : "justify-center mx-auto w-12 px-0",
      isActive
        ? "bg-[#60a5fa]/20 text-white"
        : "text-white/90 hover:text-white hover:bg-white/5"
    );

    const iconClasses = cn(
      "p-2 rounded-lg transition-all duration-200 shrink-0",
      isActive
        ? "bg-gradient-to-br from-[#60a5fa] to-[#3b82f6]"
        : "bg-[#2d5a8f]/40"
    );

    if (item.isExternal) {
      return (
        <a
          key={item.href}
          href={item.href}
          className={linkClasses}
        >
          <div className={iconClasses}>
            <item.icon className="h-5 w-5" />
          </div>
          {showLabels && (
            <span className="text-sm whitespace-nowrap">
              {item.title}
            </span>
          )}
        </a>
      );
    }

    return (
      <NavLink
        key={item.href}
        to={item.href!}
        className={linkClasses}
      >
        <div className={iconClasses}>
          <item.icon className="h-5 w-5" />
        </div>
        {showLabels && (
          <span className="text-sm whitespace-nowrap">
            {item.title}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile Menu Button */}
      {/* CHANGED: bg-gradient updated from CSS var to CRM colors */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 right-4 z-50 p-2 rounded-lg bg-gradient-to-b from-[#1a3a6b] to-[#1a3a6b] text-white shadow-lg"
        aria-label="Toggle mobile menu"
      >
        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          onClick={() => setIsMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
        />
      )}

      {/* Sidebar - Desktop */}
      {/* CHANGED: bg-gradient from CSS vars → CRM exact colors: from-[#1a3a6b] via-[#1e4a7f] to-[#1a3a6b] */}
      <aside
        className={cn(
          "hidden lg:flex flex-col bg-gradient-to-b from-[#1a3a6b] via-[#1e4a7f] to-[#1a3a6b] text-white transition-all duration-300 ease-in-out",
          showLabels ? "w-64" : "w-20"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Module Switcher */}
        <div className={cn(
          "flex items-center py-6 mb-2 transition-all duration-200",
          showLabels ? "px-6 justify-start" : "px-2 justify-center"
        )}>
          <ModuleSwitcher showLabels={showLabels} />
        </div>

        {/* Toggle Button */}
        <ManagerViewToggle open={showLabels} />

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto scrollbar-hide">
          {filteredNavigationItems.map(renderNavItem)}
        </nav>
      </aside>

      {/* Sidebar - Mobile */}
      {/* CHANGED: bg-gradient from CSS vars → CRM exact colors: from-[#1a3a6b] via-[#1e4a7f] to-[#1a3a6b] */}
      <aside
        className={cn(
          "lg:hidden fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-[#1a3a6b] via-[#1e4a7f] to-[#1a3a6b] text-white z-50 transition-transform duration-300 flex flex-col",
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Mobile sidebar always shows labels */}
        <>
          {/* Logo/Brand */}
          <div className="flex items-center py-6 mb-2 px-6 justify-between">
            <div className="font-bold text-xl text-white truncate">
              {companySettings?.companyName || 'HR Module'}
            </div>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors flex-shrink-0 ml-2"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto scrollbar-hide">
            {filteredNavigationItems.map((item) => {
              const isActive = item.href ? location.pathname === item.href : false;
              const isMenuOpen = openMenus[item.title.toLowerCase()];
              const hasChildren = item.children && item.children.length > 0;
              // FIX: only true when a child route is the current page
              const isParentActive = isChildActive(item.children);

              if (hasChildren) {
                return (
                  <div key={item.title} className="space-y-1">
                    <button
                      onClick={() => toggleMenu(item.title.toLowerCase())}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 group mx-2 w-[calc(100%-16px)]",
                        // FIX: only light-blue when a child is actually active
                        isParentActive
                          ? "bg-[#60a5fa]/20 text-white"
                          : "text-white/90 hover:bg-white/5 hover:text-white"
                      )}
                    >
                      <div className={cn(
                        "p-2 rounded-lg transition-all duration-200 shrink-0",
                        // FIX: blue gradient icon only when child is active
                        isParentActive
                          ? "bg-gradient-to-br from-[#60a5fa] to-[#3b82f6]"
                          : "bg-[#2d5a8f]/40"
                      )}>
                        <item.icon className="h-5 w-5" />
                      </div>
                      <span className="text-sm whitespace-nowrap overflow-hidden">{item.title}</span>
                      <ChevronRight className={cn("w-4 h-4 ml-auto transition-transform", isMenuOpen && "rotate-90")} />
                    </button>
                    {isMenuOpen && (
                      <div className="ml-4 space-y-1 border-l-2 border-white/10 pl-2 mb-2">
                        {item.children!.map((child) => (
                          <NavLink
                            key={child.href}
                            to={child.href!}
                            onClick={() => setIsMobileOpen(false)}
                            className={cn(
                              "flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200",
                              location.pathname === child.href
                                ? "text-white bg-[#60a5fa]/20"
                                : "text-white/70 hover:bg-white/5 hover:text-white"
                            )}
                          >
                            <span>{child.title}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              const linkClasses = cn(
                "flex items-center gap-3 p-3 rounded-lg transition-all duration-200 group mx-2 w-[calc(100%-16px)]",
                isActive
                  ? "bg-[#60a5fa]/20 text-white"
                  : "text-white/90 hover:bg-white/5 hover:text-white"
              );

              const iconClasses = cn(
                "p-2 rounded-lg transition-all duration-200 shrink-0",
                isActive
                  ? "bg-gradient-to-br from-[#60a5fa] to-[#3b82f6]"
                  : "bg-[#2d5a8f]/40"
              );

              if (item.isExternal) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsMobileOpen(false)}
                    className={linkClasses}
                  >
                    <div className={iconClasses}>
                      <item.icon className="h-5 w-5" />
                    </div>
                    <span className="text-sm whitespace-nowrap overflow-hidden">{item.title}</span>
                  </a>
                );
              }

              return (
                <NavLink
                  key={item.href}
                  to={item.href!}
                  onClick={() => setIsMobileOpen(false)}
                  className={linkClasses}
                >
                  <div className={iconClasses}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <span className="text-sm whitespace-nowrap overflow-hidden">{item.title}</span>
                </NavLink>
              );
            })}
          </nav>
        </>
      </aside>
    </>
  );
};

export default Sidebar;
